const MARKDOWN_LINK_REGEX = /(\[[^\]]+\]\([^)]+\))/g;
const MARKDOWN_LINK_CAPTURE = /^\[([^\]]+)\]\(([^)]+)\)$/;

export interface ExtractedLink {
  label: string;
  url: string;
  domain: string;
}

/** An `ExtractedLink` with the citation number its inline badge and sources entry share. */
export interface NumberedLink extends ExtractedLink {
  number: number;
}

const parseLink = (part: string): ExtractedLink | null => {
  const match = part.match(MARKDOWN_LINK_CAPTURE);
  if (!match) return null;
  const url = match[2];
  if (!/^https?:\/\//i.test(url)) return null;
  try {
    return { label: match[1], url, domain: new URL(url).hostname.replace(/^www\./, '') };
  } catch {
    return null;
  }
};

export const extractLinks = (text: string): ExtractedLink[] => {
  const parts = text.match(MARKDOWN_LINK_REGEX) ?? [];
  return parts.map(parseLink).filter((link): link is ExtractedLink => link !== null);
};

/**
 * Source prose often wraps a citation in prose parentheses — `growth as well.
 * ([1](https://…))` — which read as stray punctuation around a badge that is already visually
 * self-contained. Unwrap a paren group whose entire content is links, and drop the space that
 * preceded it so the badge sits tight against the word before it.
 */
export const unwrapCitationParens = (text: string): string =>
  text.replace(/[ \t]*\((\s*(?:\[[^\]]+\]\([^)]+\)[,;\s]*)+)\)/g, (match, inner: string) =>
    extractLinks(inner).length > 0 ? inner.trim() : match,
  );

/**
 * The citation list backing both the inline badges and the export sources box: one entry per
 * unique URL, numbered by first appearance so a URL cited twice carries the same number both
 * times.
 */
export const numberLinks = (text: string): NumberedLink[] => {
  const byUrl = new Map<string, NumberedLink>();
  for (const link of extractLinks(text)) {
    if (!byUrl.has(link.url)) byUrl.set(link.url, { ...link, number: byUrl.size + 1 });
  }
  return [...byUrl.values()];
};
